/**
 * The hymnal.
 *
 * Tunes, not recordings. Every melody here was written before 1900 and is in
 * the public domain — and because the app *synthesises* them from this note
 * data rather than playing a file, there is no performance to licence either.
 * A recording of a hymn belongs to whoever recorded it; a tune from 1551
 * belongs to everybody.
 *
 * It also means the whole hymnal is about six kilobytes, works offline, and
 * never expires — which the narration it replaces managed none of.
 *
 * ── The notation ─────────────────────────────────────────────────────────────
 *
 * Each note is `[pitch, beats]` or `[pitch, beats, chord]`. The chord sets the
 * harmony from that beat until the next one changes it, and the engine voices
 * it underneath the melody. `null` as a pitch is a rest.
 *
 * Pitches are scientific names — `G4` is the G below middle C's octave, `D5`
 * the D above. Beats are in the tune's own metre: `beatsPerBar: 3` and a note
 * of 2 is a half-bar-and-a-bit, as it would be on the page.
 *
 * ── Getting a note wrong ─────────────────────────────────────────────────────
 *
 * These were written down from knowledge of the tunes, and `npm run check:hymns`
 * verifies what can be verified mechanically: that the phrase lengths match the
 * metre, that nothing strays out of key, that the leaps are singable, and that
 * phrases land where a hymn phrase should. What it cannot check is whether a
 * note is simply the wrong one, so if something sounds off, it is one line
 * here — the tunes are written out plainly for exactly that reason.
 */

/**
 * @typedef {object} Hymn
 * @property {string} id
 * @property {string} title    what people call it
 * @property {string} tune     the tune's own name, as hymnals list it
 * @property {string} source   composer and date, for the public domain claim
 * @property {string} meter    syllables per line, which the checker uses
 * @property {string} key
 * @property {number} tempo    beats per minute
 * @property {number} beatsPerBar
 * @property {Array<[string|null, number, string?]>} notes
 */

/** @type {Hymn[]} */
export const HYMNS = [
  {
    id: 'new-britain',
    title: 'Amazing Grace',
    tune: 'NEW BRITAIN',
    source: 'Columbian Harmony, 1829',
    meter: '8.6.8.6',
    key: 'G',
    tempo: 84,
    beatsPerBar: 3,
    // Pentatonic throughout — no fourth or seventh degree anywhere, which is
    // the shape-note sound and the reason it carries without harmony.
    notes: [
      ['D4', 1, 'G'],
      ['G4', 2], ['B4', 1],
      ['G4', 2, 'G'], ['B4', 1],
      ['A4', 3, 'D'],
      ['D4', 1], ['G4', 2, 'G'], ['B4', 1],
      ['G4', 2], ['B4', 1],
      ['A4', 2, 'D'], ['D5', 1, 'G'],
      [null, 3],

      ['D5', 2, 'G'], ['B4', 1],
      ['D5', 2, 'Em'], ['B4', 1],
      ['D5', 2, 'D'], ['A4', 1],
      ['G4', 3, 'G'],
      ['E4', 1], ['G4', 2, 'C'], ['E4', 1],
      ['D4', 2, 'G'], ['E4', 1],
      ['G4', 3, 'G'],
      [null, 3],
    ],
  },

  {
    id: 'old-hundredth',
    title: 'All People That on Earth Do Dwell',
    tune: 'OLD HUNDREDTH',
    source: 'Louis Bourgeois, Genevan Psalter, 1551',
    meter: '8.8.8.8',
    key: 'G',
    tempo: 66,
    beatsPerBar: 4,
    // The oldest thing in the app by three centuries, and still the plainest:
    // one note a syllable, almost all of it stepwise.
    notes: [
      ['G4', 4, 'G'], ['G4', 4], ['F#4', 4, 'D'], ['E4', 4, 'C'],
      ['D4', 4, 'G'], ['G4', 4], ['A4', 4, 'Am'], ['B4', 4, 'G'],
      [null, 2],

      ['B4', 4, 'G'], ['A4', 4, 'D'], ['B4', 4, 'G'], ['C5', 4, 'C'],
      ['B4', 4, 'G'], ['A4', 4, 'D'], ['G4', 4, 'G'], ['G4', 4],
      [null, 2],

      ['B4', 4, 'Em'], ['B4', 4], ['C5', 4, 'C'], ['B4', 4, 'G'],
      ['A4', 4, 'D'], ['G4', 4, 'G'], ['A4', 4, 'Am'], ['B4', 4, 'D'],
      [null, 2],

      ['G4', 4, 'G'], ['E4', 4, 'C'], ['D4', 4, 'G'], ['C4', 4, 'C'],
      ['D4', 4, 'D'], ['E4', 4, 'Em'], ['D4', 4, 'D'], ['G4', 6, 'G'],
      [null, 4],
    ],
  },

  {
    id: 'slane',
    title: 'Be Thou My Vision',
    tune: 'SLANE',
    source: 'Irish traditional, before 1800',
    meter: '10.10.10.10',
    key: 'D',
    tempo: 92,
    beatsPerBar: 3,
    // Modal rather than major — the seventh is flat where the ear expects a
    // leading note, which is what makes it sound older than it is.
    notes: [
      ['D4', 1, 'D'], ['D4', 1], ['E4', 1],
      ['F#4', 2, 'D'], ['E4', 1],
      ['D4', 2, 'Bm'], ['E4', 1],
      ['F#4', 3, 'D'],
      ['A4', 2, 'A'], ['G4', 1],
      ['F#4', 3, 'D'],
      [null, 3],

      ['F#4', 1, 'Bm'], ['G4', 1], ['A4', 1],
      ['B4', 2, 'G'], ['A4', 1],
      ['G4', 2, 'Bm'], ['F#4', 1],
      ['E4', 3, 'A'],
      ['D4', 2, 'D'], ['E4', 1],
      ['D4', 3, 'D'],
      [null, 3],

      ['A4', 1, 'A'], ['A4', 1], ['B4', 1],
      ['D5', 2, 'D'], ['B4', 1],
      ['A4', 2, 'G'], ['B4', 1],
      ['A4', 3, 'A'],
      ['F#4', 2, 'D'], ['E4', 1],
      ['D4', 3, 'D'],
      [null, 3],
    ],
  },

  {
    id: 'nicaea',
    title: 'Holy, Holy, Holy',
    tune: 'NICAEA',
    source: 'John Bacchus Dykes, 1861',
    meter: '11.12.12.10',
    key: 'D',
    tempo: 80,
    beatsPerBar: 4,
    // The opening is a rising arpeggio of the tonic triad — the most confident
    // four notes in the English hymnal.
    notes: [
      ['D4', 2, 'D'], ['D4', 1], ['F#4', 1],
      ['F#4', 2, 'D'], ['A4', 1], ['A4', 1],
      ['B4', 2, 'Bm'], ['A4', 2],
      ['F#4', 4, 'D'],
      [null, 2],

      ['D4', 2, 'D'], ['D4', 1], ['F#4', 1],
      ['F#4', 2, 'D'], ['A4', 1], ['A4', 1],
      ['B4', 2, 'G'], ['A4', 2, 'A'],
      ['D5', 4, 'D'],
      [null, 2],

      ['D5', 2, 'D'], ['D5', 1], ['C#5', 1, 'A'],
      ['B4', 2, 'Bm'], ['B4', 1], ['A4', 1],
      ['G4', 2, 'G'], ['G4', 1], ['F#4', 1, 'D'],
      ['E4', 4, 'A'],
      [null, 2],

      ['D4', 2, 'D'], ['D4', 1], ['F#4', 1],
      ['A4', 2, 'D'], ['G4', 1], ['F#4', 1, 'G'],
      ['E4', 2, 'A'], ['E4', 2],
      ['D4', 6, 'D'],
      [null, 4],
    ],
  },

  {
    id: 'eventide',
    title: 'Abide With Me',
    tune: 'EVENTIDE',
    source: 'William Henry Monk, 1861',
    meter: '10.10.10.10',
    key: 'G',
    tempo: 62,
    beatsPerBar: 4,
    notes: [
      ['G4', 4, 'G'], ['G4', 2], ['A4', 2, 'D'],
      ['B4', 4, 'G'], ['A4', 4, 'D'],
      ['G4', 4, 'G'], ['E4', 4, 'C'],
      ['D4', 6, 'G'], [null, 2],

      ['D4', 4, 'G'], ['G4', 2], ['A4', 2, 'C'],
      ['B4', 4, 'G'], ['C5', 4, 'C'],
      ['B4', 4, 'G'], ['A4', 4, 'D'],
      ['G4', 6, 'G'], [null, 2],

      ['B4', 4, 'Em'], ['B4', 2], ['C5', 2, 'C'],
      ['D5', 4, 'G'], ['C5', 4, 'C'],
      ['B4', 4, 'G'], ['A4', 4, 'D'],
      ['B4', 6, 'Em'], [null, 2],

      ['G4', 4, 'C'], ['A4', 2, 'D'], ['B4', 2],
      ['A4', 4, 'D'], ['G4', 4, 'G'],
      ['F#4', 4, 'D'], ['A4', 4, 'D'],
      ['G4', 8, 'G'], [null, 4],
    ],
  },

  {
    id: 'st-anne',
    title: 'O God, Our Help in Ages Past',
    tune: 'ST. ANNE',
    source: 'William Croft, 1708',
    meter: '8.6.8.6',
    key: 'C',
    tempo: 70,
    beatsPerBar: 4,
    notes: [
      ['C4', 4, 'C'], ['E4', 4, 'C'], ['D4', 4, 'G'], ['C4', 4, 'C'],
      ['E4', 4, 'Am'], ['F4', 4, 'F'], ['G4', 6, 'G'], [null, 2],

      ['A4', 4, 'F'], ['G4', 4, 'C'], ['E4', 4, 'Am'], ['C4', 4, 'C'],
      ['D4', 4, 'G'], ['E4', 4, 'C'], ['C4', 6, 'C'], [null, 2],

      ['E4', 4, 'C'], ['A4', 4, 'F'], ['G4', 4, 'C'], ['F4', 4, 'F'],
      ['E4', 4, 'C'], ['F4', 4, 'Dm'], ['G4', 6, 'G'], [null, 2],

      ['A4', 4, 'F'], ['G4', 4, 'C'], ['F4', 4, 'Dm'], ['E4', 4, 'C'],
      ['D4', 4, 'G'], ['D4', 4, 'G'], ['C4', 8, 'C'], [null, 4],
    ],
  },

  {
    id: 'nettleton',
    title: 'Come Thou Fount of Every Blessing',
    tune: 'NETTLETON',
    source: 'John Wyeth, Repository of Sacred Music, 1813',
    meter: '8.7.8.7',
    key: 'D',
    tempo: 88,
    beatsPerBar: 4,
    notes: [
      ['F#4', 2, 'D'], ['F#4', 1], ['G4', 1],
      ['A4', 2, 'D'], ['A4', 1], ['B4', 1],
      ['A4', 2, 'A'], ['F#4', 1], ['E4', 1],
      ['D4', 4, 'D'],
      ['F#4', 2, 'D'], ['F#4', 1], ['G4', 1],
      ['A4', 2, 'D'], ['A4', 1], ['B4', 1],
      ['A4', 2, 'A'], ['F#4', 2],
      ['E4', 4, 'A'],
      [null, 4],

      ['A4', 2, 'D'], ['A4', 1], ['B4', 1],
      ['D5', 2, 'D'], ['D5', 1], ['B4', 1],
      ['A4', 2, 'G'], ['F#4', 1], ['E4', 1],
      ['D4', 4, 'D'],
      ['F#4', 2, 'D'], ['F#4', 1], ['G4', 1],
      ['A4', 2, 'G'], ['F#4', 1], ['E4', 1],
      ['D4', 2, 'A'], ['E4', 2],
      ['D4', 4, 'D'],
      [null, 4],
    ],
  },

  {
    id: 'lobe-den-herren',
    title: 'Praise to the Lord, the Almighty',
    tune: 'LOBE DEN HERREN',
    source: 'Stralsund Gesangbuch, 1665',
    meter: '14.14.4.7.8',
    key: 'G',
    tempo: 96,
    beatsPerBar: 3,
    notes: [
      ['G4', 2, 'G'], ['G4', 1],
      ['D5', 2, 'G'], ['D5', 1],
      ['B4', 3, 'G'],
      ['D5', 2, 'D'], ['C5', 1],
      ['B4', 2, 'G'], ['A4', 1],
      ['G4', 3, 'G'],
      [null, 3],

      ['G4', 2, 'G'], ['G4', 1],
      ['D5', 2, 'G'], ['D5', 1],
      ['B4', 3, 'G'],
      ['D5', 2, 'D'], ['C5', 1],
      ['B4', 2, 'G'], ['A4', 1],
      ['G4', 3, 'G'],
      [null, 3],

      ['D5', 2, 'D'], ['D5', 1],
      ['E5', 3, 'Em'],
      ['C5', 2, 'C'], ['C5', 1],
      ['B4', 3, 'G'],
      ['A4', 2, 'D'], ['B4', 1],
      ['C5', 2, 'C'], ['A4', 1],
      ['G4', 3, 'G'],
      [null, 3],
    ],
  },
];

export const HYMNS_BY_ID = Object.fromEntries(HYMNS.map((h) => [h.id, h]));

/** How long a hymn runs, in seconds. */
export function hymnSeconds(hymn) {
  const beats = hymn.notes.reduce((total, [, length]) => total + length, 0);
  return (beats / hymn.tempo) * 60;
}
